import React, { useMemo, useState } from 'react';
import { useStationMode } from '../../Common/useStationMode';
import {
  ArrowRight,
  Building2,
  CalendarDays,
  Check,
  ChevronLeft,
  CircleDollarSign,
  ExternalLink,
  History,
  HelpCircle,
  MessageSquare,
  PackageCheck,
  ShoppingBasket,
  Users,
  Utensils,
} from 'lucide-react';
import LocalBreakfastPreview from './LocalBreakfastPreview';

const ExampleFrame = ({ title, children }) => (
  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
    <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2.5">
      <span className="text-xs font-semibold text-gray-700">{title}</span>
      <span className="text-[11px] text-gray-400">Vereinfachter Ausschnitt · Beispieldaten</span>
    </div>
    <div className="p-4 sm:p-5">{children}</div>
  </div>
);

const EmployeeExample = () => (
  <ExampleFrame title="Mitarbeiter-Dashboard">
    <div className="grid gap-4 sm:grid-cols-[1fr_0.9fr]">
      <div className="rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div><div className="text-sm font-semibold text-gray-900">Anna Beispiel</div><div className="text-xs text-gray-500">1. Wachabteilung</div></div>
          <div className="text-right"><div className="text-xs text-gray-500">Gesamtsaldo</div><div className="font-semibold text-red-700">−4,80 €</div></div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2 text-xs"><div className="rounded-md bg-gray-50 p-2"><span className="text-gray-500">Frühstück</span><div className="mt-1 font-semibold text-gray-900">−3,30 €</div></div><div className="rounded-md bg-gray-50 p-2"><span className="text-gray-500">Getränke</span><div className="mt-1 font-semibold text-gray-900">−1,50 €</div></div></div>
      </div>
      <div className="rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-900"><History className="h-4 w-4" /> Letzte Buchungen</div>
        <div className="mt-3 space-y-2 text-xs"><div className="flex justify-between"><span className="text-gray-600">Frühstück · heute</span><span className="font-medium">−1,80 €</span></div><div className="flex justify-between"><span className="text-gray-600">Getränke · gestern</span><span className="font-medium">−1,50 €</span></div></div>
      </div>
    </div>
  </ExampleFrame>
);

const MealOverviewExample = () => (
  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
    <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-4 text-white">
      <div className="text-base font-semibold">Essensübersicht (Tagesansicht)</div>
      <div className="mt-1 text-xs text-blue-100">Donnerstag, 7. August</div>
    </div>
    <div className="grid gap-4 p-4 sm:grid-cols-[1.2fr_0.8fr]">
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
        <div className="text-sm font-semibold text-emerald-800">Einkaufsliste</div>
        <div className="mt-3 space-y-3 rounded-lg border border-emerald-100 bg-white p-3">
          <div><div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Brötchen gesamt</div><div className="mt-1 text-sm text-gray-700"><span className="mr-2 text-xl font-semibold text-emerald-600">6</span>Helle Brötchen</div></div>
          <div className="border-t border-gray-100 pt-2"><div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Beläge</div><div className="mt-1 grid grid-cols-2 gap-1 text-xs text-gray-600"><span>4x Käse</span><span>3x Wurst</span><span>2x Marmelade</span><span>1x Mett · 50 g</span></div></div>
        </div>
      </div>
      <div className="space-y-3">
        <div className="rounded-xl border border-violet-200 bg-violet-50 p-4"><div className="text-xs font-medium text-violet-700">Mittagessen</div><div className="mt-1 text-xl font-semibold text-violet-900">12 Portionen</div></div>
        <div className="rounded-xl border border-gray-200 p-4"><div className="text-xs font-medium text-gray-500">Bestellungen</div><div className="mt-1 text-xl font-semibold text-gray-900">8 Mitarbeiter</div></div>
      </div>
    </div>
  </div>
);

const MealPlanningHints = () => (
  <div className="mt-4 space-y-2 text-sm leading-6 text-gray-600">
    <p><strong className="text-gray-900">Rezepte:</strong> Hinterlege unter „Rezepte verwalten“ Zutaten und Zubereitung eurer Lieblingsgerichte zum Nachlesen für die Wachabteilung.</p>
    <p><strong className="text-gray-900">Gemeinsame Einkaufsliste:</strong> Mitarbeiter können benötigte Artikel mit Menge und Einheit ergänzen und Erledigtes abhaken.</p>
    <p className="text-xs text-gray-500">Beide Funktionen kannst du in den Einstellungen für das Mitarbeiter-Dashboard freischalten.</p>
  </div>
);

const MealControlExample = () => (
  <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
    <div className="space-y-5">
      <div className="grid gap-2 sm:grid-cols-[150px_1fr]">
        <div className="text-sm font-semibold text-gray-900">Tagespreis festlegen</div>
        <div>
          <p className="text-sm leading-6 text-gray-600">Der Admin trägt den Preis für Mittag- und Abendessen selbst ein. Der eingestellte Preis gilt rückwirkend für alle Bestellungen des jeweiligen Tages.</p>
          <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-700">
            <div className="font-medium text-gray-900">Beispielrechnung</div>
            <div className="mt-1">50,00 € Einkauf ÷ 10 mitessende Mitarbeiter = <strong>5,00 € pro Person</strong></div>
          </div>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <span className="rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-gray-700">Normal · 5,00 €</span>
            <span className="rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-gray-700">Vegetarisch · eigener Preis</span>
          </div>
          <p className="mt-2 text-xs leading-5 text-gray-500">Bei Bedarf können mehrere Varianten mit unterschiedlichen Preisen angelegt werden.</p>
        </div>
      </div>
      <div className="border-t border-gray-200 pt-5 grid gap-2 sm:grid-cols-[150px_1fr]"><div className="text-sm font-semibold text-gray-900">Ausgabe festlegen</div><p className="text-sm leading-6 text-gray-600">Frühstück, Mittag- und Abendessen können getrennt ausgegeben werden. Dabei wird frei ausgewählt, welche Bestandteile übernommen werden und für welche Mitarbeiter die Ausgabe gilt.</p></div>
      <div className="border-t border-gray-200 pt-5 grid gap-2 sm:grid-cols-[150px_1fr]"><div className="text-sm font-semibold text-gray-900">Sponsoring</div><p className="text-sm leading-6 text-gray-600">Die ausgewählte Person übernimmt die konfigurierten Kosten. Vor dem Auslösen sollte deshalb genau geprüft werden, was und wer in der Ausgabe enthalten ist.</p></div>
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-950">
        <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide"><span aria-hidden="true">!</span> Wichtig</div>
        <p className="mt-2 text-sm font-medium leading-6">Sobald das Sponsoring ausgelöst wurde, wird die zugehörige Bestellmaske für Frühstück, Mittag- oder Abendessen für diesen Tag gesperrt.</p>
        <p className="mt-1 text-sm leading-6">Das Sponsoring und die Sperre der Bestellmaske können für diesen Tag aus Sicherheitsgründen nicht rückgängig gemacht oder wiederhergestellt werden.</p>
      </div>
    </div>
  </div>
);

const MenuStockExample = () => (
  <ExampleFrame title="Menü & Preise">
    <div className="grid gap-4 sm:grid-cols-[150px_1fr]">
      <div className="space-y-1 text-xs"><div className="rounded-md bg-slate-900 px-3 py-2 font-medium text-white">Getränke</div><div className="px-3 py-2 text-gray-600">Snacks</div><div className="px-3 py-2 text-gray-600">Eis</div><div className="px-3 py-2 text-gray-600">+ Kategorie</div></div>
      <div className="space-y-2">{[['Wasser', '1,50 €', 'Unbegrenzt'], ['Apfelschorle', '1,80 €', '12 auf Lager']].map(([name, price, stock]) => <div key={name} className="flex items-center justify-between rounded-lg border border-gray-200 p-3"><div><div className="text-sm font-medium text-gray-900">{name}</div><div className="text-xs text-gray-500">{stock}</div></div><div className="text-sm font-semibold text-gray-900">{price}</div></div>)}</div>
    </div>
  </ExampleFrame>
);

const CrossDepartmentExample = () => (
  <ExampleFrame title="Übergreifende Mitarbeiter">
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-3"><div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100"><Users className="h-4 w-4 text-gray-700" /></div><div><div className="text-sm font-semibold text-gray-900">Chris Beispiel</div><div className="text-xs text-gray-500">Übergreifend · keiner Wachabteilung fest zugeordnet</div></div></div>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {[
          ['1. Wachabteilung', '−1,80 €'],
          ['2. Wachabteilung', '−2,40 €'],
          ['3. Wachabteilung', '−1,20 €'],
          ['4. Wachabteilung', '0,00 €'],
        ].map(([department, balance]) => (
          <div key={department} className="rounded-md bg-gray-50 p-3">
            <div className="text-xs text-gray-500">{department}</div>
            <div className="mt-1 text-sm font-semibold text-gray-900">{balance}</div>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs leading-5 text-gray-500">Jede Buchung landet im Konto der Wachabteilung, in der sie entstanden ist. Alle vier Konten sind gleichberechtigt.</p>
    </div>
  </ExampleFrame>
);

const TutorialCompleteExample = () => (
  <div className="rounded-xl border border-gray-200 bg-gray-50 p-5 sm:p-6">
    <div className="grid gap-3 sm:grid-cols-3">
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <MessageSquare className="h-5 w-5 text-slate-700" />
        <div className="mt-3 text-sm font-semibold text-gray-900">Feedback geben</div>
        <p className="mt-1 text-xs leading-5 text-gray-500">Fragen, Fehler und Verbesserungsvorschläge können direkt über das Feedback-Modul gesendet werden.</p>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <Users className="h-5 w-5 text-slate-700" />
        <div className="mt-3 text-sm font-semibold text-gray-900">Direkter Kontakt</div>
        <p className="mt-1 text-xs leading-5 text-gray-500">Alternativ kannst du dich mit Fragen oder Vorschlägen direkt an den Gründer von Fw-Kantine wenden.</p>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <HelpCircle className="h-5 w-5 text-slate-700" />
        <div className="mt-3 text-sm font-semibold text-gray-900">Hilfe &amp; Neues</div>
        <p className="mt-1 text-xs leading-5 text-gray-500">Dort findest du aktuelle Informationen, Neuerungen und wichtige Hinweise zur Anwendung.</p>
      </div>
    </div>
  </div>
);

const PayPalExample = () => (
  <ExampleFrame title="Externer PayPal-Link">
    <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-center">
      <div><div className="text-sm font-semibold text-gray-900">Saldo ausgleichen</div><p className="mt-1 text-xs leading-5 text-gray-500">Der hinterlegte Link öffnet das PayPal-Konto eines Mitarbeiters. Der Betrag wird dort eigenständig über „Freunde und Familie“ gesendet.</p></div>
      <div className="inline-flex items-center justify-center gap-2 rounded-md bg-slate-800 px-4 py-2.5 text-xs font-semibold text-white">PayPal öffnen <ExternalLink className="h-3.5 w-3.5" /></div>
    </div>
    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs leading-5 text-gray-600">Die Kantinen-App verarbeitet keine Zahlung und bestätigt keinen Zahlungseingang. Der Saldo wird weiterhin durch einen Admin verbucht.</div>
  </ExampleFrame>
);

const pages = [
  { icon: Users, title: 'Mitarbeiter, Salden und Verlauf', text: 'Jeder Mitarbeiter erhält eine eigene Übersicht. Dort sind Salden, Buchungen und Stornierungen nachvollziehbar gebündelt.', visual: <EmployeeExample /> },
  { icon: ShoppingBasket, title: 'Frühstück sicher ausprobieren', text: 'Teste Mengen und Preise wie in der echten Frühstücksbestellung. Diese Vorschau bleibt vollständig lokal.', visual: <LocalBreakfastPreview /> },
  { icon: CalendarDays, title: 'Essensübersicht im Alltag', text: 'Die Tagesansicht zeigt auf einen Blick, was bestellt wurde, wie viele Portionen benötigt werden und was einzukaufen ist.', visual: <><MealOverviewExample /><MealPlanningHints /></> },
  { icon: Utensils, title: 'Preise, Ausgabe und Sponsoring', text: 'Mittag- und Abendessen erhalten einen selbst berechneten Tagespreis. Varianten können unterschiedliche Preise haben. Anschließend lassen sich Mahlzeiten gezielt ausgeben oder sponsern.', visual: <MealControlExample /> },
  { icon: PackageCheck, title: 'Kategorien, Preise und Lager', text: 'Produkte lassen sich in eigene Kategorien gliedern und wahlweise mit einem automatisch reduzierten Lagerbestand führen.', visual: <MenuStockExample /> },
  { crossDepartment: true, icon: Building2, title: 'Übergreifende Mitarbeiter', text: 'Übergreifende Mitarbeiter haben keine Stammabteilung. Sie sind in allen Wachabteilungen sichtbar und besitzen vier feste, gleichberechtigte Konten – eines je Wachabteilung.', visual: <CrossDepartmentExample /> },
  { icon: CircleDollarSign, title: 'PayPal ist nur eine Verknüpfung', text: 'Die App zeigt lediglich einen externen PayPal-Link an. Es findet keine Zahlungsabwicklung innerhalb der Kantinen-App statt.', visual: <PayPalExample /> },
  { icon: Check, title: 'Tutorial abgeschlossen', text: 'Du kennst jetzt die wichtigsten Bereiche von Fw-Kantine. Bei Fragen oder Vorschlägen stehen dir mehrere Wege offen.', visual: <TutorialCompleteExample /> },
];

const GuidedTutorial = ({ onDone, onSkip, initialPage = 0 }) => {
  const { isSingleDepartment } = useStationMode();
  const visiblePages = pages.filter(page => !isSingleDepartment || !page.crossDepartment);
  const safeInitialPage = Math.min(Math.max(initialPage, 0), visiblePages.length - 1);
  const [pageIndex, setPageIndex] = useState(safeInitialPage);
  const visiblePageIndex = Math.min(pageIndex, visiblePages.length - 1);
  const page = visiblePages[visiblePageIndex];
  const Icon = page.icon;
  const isLast = visiblePageIndex === visiblePages.length - 1;
  const progressLabel = useMemo(() => `${visiblePageIndex + 1} von ${visiblePages.length}`, [visiblePageIndex, visiblePages.length]);

  return (
    <div>
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white"><Icon className="h-5 w-5" /></div>
        <div><div className="text-xs font-semibold text-gray-400">{progressLabel}</div><h2 className="mt-1 text-2xl font-semibold tracking-tight text-gray-950">{page.title}</h2><p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600">{page.text}</p></div>
      </div>

      <div key={pageIndex} className="mt-6">{page.visual}</div>

      <div className="mt-6 flex flex-col-reverse gap-3 border-t border-gray-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          <button type="button" onClick={onSkip} className="rounded-lg px-3 py-2.5 text-sm font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-800">Tutorial beenden</button>
          {visiblePageIndex > 0 && <button type="button" onClick={() => setPageIndex(visiblePageIndex - 1)} className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"><ChevronLeft className="h-4 w-4" /> Zurück</button>}
        </div>
        <button type="button" onClick={() => isLast ? onDone?.() : setPageIndex(visiblePageIndex + 1)} className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-800 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-700 active:scale-[0.98]">{isLast ? <><Check className="h-4 w-4" /> Tutorial abschließen</> : <>Weiter <ArrowRight className="h-4 w-4" /></>}</button>
      </div>
    </div>
  );
};

export default GuidedTutorial;
